/**
 * usePdfDocument — ouvrir un PDF par pdf.js, pour la visionneuse (`PdfDisplay.web`) et le PDF posé
 * dans la page (`PdfInline.web`).
 *
 * Un document ouvert par adresse, partagé : l'énoncé posé dans la page et la visionneuse qu'un appui
 * ouvre lisent le même — elle s'ouvre chargée, sans second téléchargement ni seconde copie en
 * mémoire —, et il se libère quand plus personne ne le montre. Un document qui a échoué est oublié :
 * le prochain affichage retente.
 *
 * L'échec ne se dit que si pdf.js échoue, ou si plus rien n'avance pendant `STALL_MS` : un gros
 * fichier qui arrive n'échoue pas, et un PDF qui arrive après le repli s'affiche quand même. Il rend
 * sa cause à `onError`.
 */
import { useEffect, useRef, useState } from "react"

import type { PdfPageSize } from "./PdfPages.web"
import { loadPdfEngine, openPdf, type PdfDocument, type PdfLoadingTask } from "./pdfjs"

/** Sans aucun signe de progrès pendant ce temps, le repli s'affiche ; le chargement continue. */
export const STALL_MS = 20_000

export type PdfLoad =
    { status: "loading" } | { status: "ready"; doc: PdfDocument; pages: PdfPageSize[] } | { status: "failed" }

const LOADING: PdfLoad = { status: "loading" }
const FAILED: PdfLoad = { status: "failed" }

type Opened = { doc: PdfDocument; pages: PdfPageSize[] }

/** Un document et ceux qui le montrent. */
type Shared = {
    ready: Promise<Opened>
    users: number
    onProgress: Set<() => void>
    task: PdfLoadingTask | null
}

const shared = new Map<string, Shared>()

/** Plus personne ne montrait le document quand la bibliothèque est arrivée : il ne s'ouvre pas. */
const RELEASED = new Error("PDF released before it opened")

function acquire(uri: string): Shared {
    const existing = shared.get(uri)
    if (existing != null) {
        existing.users += 1
        return existing
    }
    const entry: Shared = { users: 1, onProgress: new Set(), task: null, ready: Promise.resolve(null as never) }
    const progressed = () => entry.onProgress.forEach((listener) => listener())
    entry.ready = loadPdfEngine().then(async (engine) => {
        if (entry.users === 0) throw RELEASED
        progressed()
        entry.task = openPdf(engine, uri)
        entry.task.onProgress = progressed
        const doc = await entry.task.promise
        const pages = await Promise.all(
            Array.from({ length: doc.numPages }, (_, index) =>
                doc.getPage(index + 1).then((page) => {
                    const { width, height } = page.getViewport({ scale: 1 })
                    return { width, height }
                })
            )
        )
        return { doc, pages }
    })
    // Le worker, partagé par les PDF de la visite, reste : un document qui échoue (404, fichier abîmé)
    // ne doit pas laisser blanches les pages d'un autre déjà ouvert. Seul le document est oublié.
    entry.ready.catch(() => {
        if (shared.get(uri) === entry) shared.delete(uri)
    })
    shared.set(uri, entry)
    return entry
}

function release(uri: string, entry: Shared) {
    entry.users -= 1
    if (entry.users > 0) return
    if (shared.get(uri) === entry) shared.delete(uri)
    void entry.task?.destroy()
}

export function usePdfDocument(uri: string, onError?: (cause: unknown) => void): PdfLoad {
    // L'état vaut pour une adresse : celui d'une autre (le document précédent) ne se montre pas le
    // temps d'un rendu.
    const [load, setLoad] = useState<{ uri: string; load: PdfLoad }>({ uri: "", load: LOADING })
    const onErrorRef = useRef(onError)
    onErrorRef.current = onError

    useEffect(() => {
        if (uri === "") return
        let live = true
        const set = (next: PdfLoad) => {
            if (live) setLoad({ uri, load: next })
        }
        const entry = acquire(uri)

        let stall: ReturnType<typeof setTimeout> | undefined
        const progressed = () => {
            clearTimeout(stall)
            stall = setTimeout(() => set(FAILED), STALL_MS)
        }
        progressed()
        entry.onProgress.add(progressed)

        entry.ready.then(
            ({ doc, pages }) => {
                clearTimeout(stall)
                set({ status: "ready", doc, pages })
            },
            (cause: unknown) => {
                if (!live) return
                clearTimeout(stall)
                set(FAILED)
                onErrorRef.current?.(cause)
            }
        )

        return () => {
            live = false
            clearTimeout(stall)
            entry.onProgress.delete(progressed)
            release(uri, entry)
        }
    }, [uri])

    return load.uri === uri ? load.load : LOADING
}
